import {
  AvailablePreTestConfigurationOptions,
  AvailableProgressPageTestConfigurationOptions,
  CollectedTestData,
  ContrastSensitivityData,
  DyscalculiaTaskResult,
  EyeCoverAction,
  EyeType,
  GazeData,
  KineticGraphStat,
  KineticPerimetryCollectedData,
  OKNProgressSnapshot,
  OKNRawSample,
  PupilSizeData,
  ReadingAssessmentCatalogEntry,
  ScoredLetterSet,
  Snapshot,
  TestCategory,
  TestConfiguration,
  TestEventCodes,
  TestProgressConfiguration,
  TestSpecificConfig,
  TestStatus,
  TestType,
  TestUIConfiguration,
  TumblingEStatus,
  VFACollectedData,
  AssistanceVideo as VirtualAssistantVideo,
} from "types/testTypes";
import {
  ConvertVFASnapshotToCollectedData,
  GetCorrectedBlindSpotStimuliSizeConfigs,
  TurnOffBlindSpotControlsOnEnteringMovingFixation,
} from "@utils/test-specific-utils/PerimetryTestUtils";
import {
  PERSISTED_CONFIG_FIELD_PATHS,
  TEST_DEFINITIONS,
} from "@constants/testConstants/testDefinitions";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { get, merge } from "lodash";

import { DEFAULT_TEST_UI_CONFIGURATION } from "@constants/testConstants/configurationHelperConstants";
import { HeadsetVersion } from "types/productTypes";
import { calculateDisplacementFromOrigin } from "@utils/TestUtils";

export interface VirtualAssistanceStatus {
  state?: string | null;
  remainingVideos?: number | null;
  currentRetryCount?: number | null;
  maxRetryCount?: number | null;
  isRetrySequence?: boolean;
  isInfiniteRetry?: boolean;
  retryVideoName?: string | null;
  doNotShowAgainList?: string[] | null;
  englishFallbackUsed?: boolean;
  isGuideVideo?: boolean;
}

export interface CalibrationLifecycleStatus {
  status: string | null;
  message: string | null;
  attempt: number | null;
  retryAttempt: number | null;
  success: boolean | null;
  outcome: string | null;
}

export interface HeadsetActiveTest {
  category: string | null;
  isResumable: boolean;
}

export interface TestState {
  testId: string | null;
  testStartDate: string | null;
  timeScale: number;
  configuration: TestConfiguration | null;
  progressConfiguration: TestConfiguration | null;
  uiConfiguration: TestUIConfiguration | null;
  testEventCodes: TestEventCodes | null;
  baseAvailableConfigurations: AvailablePreTestConfigurationOptions | null;
  availableConfigurations: AvailablePreTestConfigurationOptions | null;
  availableProgressConfigurations: AvailableProgressPageTestConfigurationOptions | null;
  status: TestStatus;
  collectedTestData: CollectedTestData | null;
  completedPercentage: number;
  isCurrentlyCalibrating: boolean;
  calibrationLifecycle: CalibrationLifecycleStatus | null;
  calibrationUnresponsive: boolean;
  calibrationFailedInRun: boolean;
  isGazeOffBounds: boolean;
  currentVirtualAssistanceVideo: string | null;
  virtualAssistanceStatus: VirtualAssistanceStatus | null;
  persistedConfiguration: Partial<TestConfiguration>;
  savedConfiguration: Partial<Record<TestCategory, Partial<TestConfiguration> | null>> | null;
  readingAssessmentCatalog: ReadingAssessmentCatalogEntry[];
  readingAssessmentCatalogError: string | null;
  isSceneReady?: boolean | null;
  testPhase: string | null;
  headsetActiveTest: HeadsetActiveTest | null;
  isResumedTestSession: boolean;
  shouldWaitForNextEyeOnFinish: boolean;
  isLastOfBackToBackEyeTest: boolean;
  isManualCompletionCheckInProgress: boolean;
  isManualCompletionCheckFailed: boolean;
  isManualCompletionCheckSucceeded: boolean;
}

const initialState: TestState = {
  testId: null,
  testStartDate: null,
  timeScale: 1,
  configuration: null,
  progressConfiguration: null,
  uiConfiguration: DEFAULT_TEST_UI_CONFIGURATION,
  testEventCodes: null,
  baseAvailableConfigurations: null,
  availableConfigurations: null,
  availableProgressConfigurations: null,
  status: TestStatus.DeviceSelection,
  collectedTestData: null,
  completedPercentage: 0,
  isCurrentlyCalibrating: false,
  calibrationLifecycle: null,
  calibrationUnresponsive: false,
  calibrationFailedInRun: false,
  isGazeOffBounds: false,
  currentVirtualAssistanceVideo: null,
  virtualAssistanceStatus: null,
  persistedConfiguration: {},
  savedConfiguration: null,
  readingAssessmentCatalog: [],
  readingAssessmentCatalogError: null,
  isSceneReady: null,
  testPhase: null,
  headsetActiveTest: null,
  isResumedTestSession: false,
  shouldWaitForNextEyeOnFinish: false,
  isLastOfBackToBackEyeTest: false,
  isManualCompletionCheckInProgress: false,
  isManualCompletionCheckFailed: false,
  isManualCompletionCheckSucceeded: false,
};

const initialVirtualAssistanceStatus: VirtualAssistanceStatus = {
  state: null,
  remainingVideos: null,
  currentRetryCount: null,
  maxRetryCount: null,
  isRetrySequence: null,
  isInfiniteRetry: null,
  retryVideoName: null,
  doNotShowAgainList: [],
};

const testSlice = createSlice({
  name: "test",
  initialState,
  reducers: {
    resetTestState: (state) => {
      return {
        ...initialState,
        persistedConfiguration: state.persistedConfiguration,
        savedConfiguration: state.savedConfiguration,
        readingAssessmentCatalog: state.readingAssessmentCatalog,
        readingAssessmentCatalogError: state.readingAssessmentCatalogError,
        virtualAssistanceStatus: {
          ...initialState.virtualAssistanceStatus,
          doNotShowAgainList: state.virtualAssistanceStatus?.doNotShowAgainList || [],
        },
      };
    },
    clearTestRelatedData(state) {
      return {
        ...initialState,
        configuration: state.configuration,
        progressConfiguration: state.progressConfiguration,
        uiConfiguration: state.uiConfiguration,
        testEventCodes: state.testEventCodes,
        baseAvailableConfigurations: state.baseAvailableConfigurations,
        availableConfigurations: state.availableConfigurations,
        availableProgressConfigurations: state.availableProgressConfigurations,
        virtualAssistanceStatus: {
          ...initialVirtualAssistanceStatus,
          doNotShowAgainList: state.virtualAssistanceStatus?.doNotShowAgainList || [], // Because we don't want to repeat the tutorial / welcome videos
        },
        status: state.status,
        persistedConfiguration: state.persistedConfiguration,
        savedConfiguration: state.savedConfiguration,
        readingAssessmentCatalog: state.readingAssessmentCatalog,
        readingAssessmentCatalogError: state.readingAssessmentCatalogError,
      };
    },
    setTestId(state, action: PayloadAction<string>) {
      state.testId = action.payload;
    },
    selectTest(state, action: PayloadAction<TestCategory>) {
      state.configuration = { name: action.payload } as TestConfiguration;
    },
    setTestUIConfiguration(state, action: PayloadAction<TestUIConfiguration>) {
      state.uiConfiguration = action.payload;
    },
    clearTestConfigurations(state, action: PayloadAction<TestCategory | null>) {
      state.configuration = { name: action.payload || null } as TestConfiguration;
      state.progressConfiguration = null;
      state.uiConfiguration = DEFAULT_TEST_UI_CONFIGURATION;
      state.baseAvailableConfigurations = null;
      state.availableConfigurations = null;
      state.availableProgressConfigurations = null;
    },
    updateTestConfiguration(state, action: PayloadAction<Partial<TestConfiguration>>) {
      let correctedUpdate = action.payload;
      if (state?.configuration?.name === TestCategory.VisualField) {
        correctedUpdate = GetCorrectedBlindSpotStimuliSizeConfigs(
          state.configuration,
          TurnOffBlindSpotControlsOnEnteringMovingFixation(state.configuration, action.payload),
        );
      }
      state.configuration = {
        ...state.configuration,
        ...correctedUpdate,
      };
    },
    updatePersistedConfigFromTestConfig(state, action: PayloadAction<Partial<TestConfiguration>>) {
      if (!state.persistedConfiguration) {
        console.warn(
          "Unexpected behaviour - low importance: Persistent config was not initialized. Setting it up now.",
        );
        state.persistedConfiguration = {};
      }

      for (const path of PERSISTED_CONFIG_FIELD_PATHS) {
        const value = get(action.payload, path);
        if (value !== undefined) {
          const pathSegments = path.split(".");
          if (pathSegments.length === 2) {
            const [parent, child] = pathSegments as [string, string];
            if (!state.persistedConfiguration[parent]) {
              (state.persistedConfiguration as any)[parent] = {};
            }
            (state.persistedConfiguration as any)[parent][child] = value;
          }
        }
      }
    },
    mergeProgressConfigToMain(state, action: PayloadAction<TestProgressConfiguration>) {
      if (!state.configuration) {
        console.warn(
          "Trying to update test configuration after progress configuration change, but test configuration is null",
        );
        return;
      }
      state.configuration = merge({}, state.configuration, action.payload);
    },
    setTestConfigurationExtraData(state, action: PayloadAction<{ extraData: TestSpecificConfig }>) {
      state.configuration = {
        ...state.configuration,
        extraData: action.payload.extraData,
      };
    },
    setTestProgressConfiguration(state, action: PayloadAction<TestConfiguration>) {
      state.progressConfiguration = action.payload;
    },
    setTestEventCodes(state, action: PayloadAction<TestEventCodes>) {
      state.testEventCodes = action.payload;
    },
    setAvailableConfigurations(
      _state,
      _action: PayloadAction<{
        availableOptions: AvailablePreTestConfigurationOptions;
        productVersion: HeadsetVersion;
        isNewTestSelection?: boolean;
      }>,
    ) {},
    setBaseAvailableConfigurations(
      state,
      action: PayloadAction<AvailablePreTestConfigurationOptions | null>,
    ) {
      state.baseAvailableConfigurations = action.payload;
    },
    applyAvailableConfigurations(
      state,
      action: PayloadAction<{
        availableOptions: AvailablePreTestConfigurationOptions;
        productVersion: HeadsetVersion;
      }>,
    ) {
      state.availableConfigurations = action.payload.availableOptions;
    },
    setAvailableProgressConfigurations(
      state,
      action: PayloadAction<{
        options: AvailableProgressPageTestConfigurationOptions;
        productVersion: HeadsetVersion;
      }>,
    ) {
      state.availableProgressConfigurations = action.payload.options;
    },
    setReadingAssessmentCatalog(state, action: PayloadAction<ReadingAssessmentCatalogEntry[]>) {
      state.readingAssessmentCatalog = action.payload;
      state.readingAssessmentCatalogError = null;
    },
    setReadingAssessmentCatalogError(state, action: PayloadAction<string | null>) {
      state.readingAssessmentCatalogError = action.payload;
    },
    setTestStatus(state, action: PayloadAction<{ status: TestStatus; extraData?: any }>) {
      state.status = action.payload.status;
    },
    resumeTest(
      state,
      action: PayloadAction<{
        testName: TestCategory;
        testConfiguration: TestConfiguration;
        collectedData: CollectedTestData | null;
        startDate?: string | null;
        testId?: string | null;
      }>,
    ) {
      const testDefinition = TEST_DEFINITIONS[action.payload.testName];
      if (action.payload.testId) {
        state.testId = action.payload.testId;
      }
      state.configuration = action.payload.testConfiguration;
      state.progressConfiguration = action.payload.testConfiguration;
      state.testEventCodes = testDefinition?.eventCodes ?? null;
      if (action.payload.collectedData) {
        state.collectedTestData = action.payload.collectedData;
      }
      if (action.payload.startDate) {
        state.testStartDate = action.payload.startDate;
      }
      state.status = TestStatus.Started;
      state.isResumedTestSession = true;
    },
    pauseTest(state) {
      if (state.status === TestStatus.Started) {
        state.status = TestStatus.Paused;
      }
    },
    unpauseTest(state) {
      if (state.status === TestStatus.Paused) {
        state.status = TestStatus.Started;
      }
    },
    resetPauseState(state) {
      state.timeScale = 1;
      if (state.configuration) {
        state.configuration = { ...state.configuration, pauseTest: false };
      }
      if (state.progressConfiguration) {
        state.progressConfiguration = { ...state.progressConfiguration, pauseTest: false };
      }
    },
    sendMessageToHeadSet(_state, _action: PayloadAction<{ extraData?: any }>) {
      // handled in sagas
    },
    updateGazeData(state, action: PayloadAction<GazeData>) {
      const newGazeData = action.payload;
      state.collectedTestData = {
        ...state.collectedTestData,
        gazeDatas: [...(state.collectedTestData?.gazeDatas || []), newGazeData],
      };
    },
    setIsGazeOffBounds(state, action: PayloadAction<boolean>) {
      state.isGazeOffBounds = action.payload;
    },
    sendTestStartMessage(_state, _action: PayloadAction<{ isRetry: boolean }>) {},
    attachToRunningTest(
      _state,
      _action: PayloadAction<{ testId: string; scene: string | null }>,
    ) {},
    sendTestTerminateMessage(_state, _action: PayloadAction<{ isRetry: boolean }>) {},
    manuallyUpdateCollectedTestData(state, action: PayloadAction<Partial<CollectedTestData>>) {
      state.collectedTestData = { ...state.collectedTestData, ...action.payload };
    },
    updateCollectedTestData(state, action: PayloadAction<Snapshot>) {
      const {
        gazeDataPoint,
        headVelocityData,
        percentagePoint,
        contrastSensitivityData,
        pupillometryData,
        ocularMotilityData,
        ocularMotilityMetrics,
        vAcuityLetterSet,
        vAcuityIndex,
        tumblingESnapshot,
        tumblingEThresholdRow,
        tumblingEThresholdSnellenSize,
        tumblingEChartStimuli,
        fdtData,
        coverTestData,
        cptSnapshot,
        stereoAcuityData,
        eyeImageData,
        gazeDistanceFromCenter: eyeDisplacement,
        kineticStimulus,
        kineticGraphStat,
        colorVisionSnapshot,
        colorVisionInitData,
        colorVisionResults,
        dyscalculiaSnapshot,
        vfaSnapshot,
        wcstSnapshot,
        readingAssessmentSnapshot,
        npctSnapshot,
        npctUserInput,
        oknSnapshot,
        antiSaccadeSample,
        smoothPursuitSnapshot,
      } = action.payload;
      if (gazeDataPoint) {
        state.collectedTestData = {
          ...state.collectedTestData,
          gazeDatas: [...(state.collectedTestData?.gazeDatas || []), gazeDataPoint],
        };
      }
      if (percentagePoint) {
        state.collectedTestData = {
          ...state.collectedTestData,
          percentagePoints: [...(state.collectedTestData?.percentagePoints || []), percentagePoint],
        };
      }
      if (headVelocityData && headVelocityData.velocity !== 0) {
        state.collectedTestData = {
          ...state.collectedTestData,
          headVelocities: [...(state.collectedTestData?.headVelocities || []), headVelocityData],
        };
      }
      if (eyeDisplacement) {
        state.collectedTestData = {
          ...state.collectedTestData,
          eyeDisplacements: [...(state.collectedTestData?.eyeDisplacements || []), eyeDisplacement],
        };
      }
      if (contrastSensitivityData) {
        // Check if it's Pelli-Robson data with proper type guarding
        if (
          typeof contrastSensitivityData === "object" &&
          contrastSensitivityData !== null &&
          ("pelliRobsonLetterSet" in contrastSensitivityData ||
            "pelliRobsonIndex" in contrastSensitivityData)
        ) {
          // Get the existing Pelli-Robson data (if any)
          const existingPelliRobsonData =
            state.collectedTestData?.contrastSensitivityOrPelliRobsonData?.pelliRobsonData || {};

          // Type assertion to help TypeScript understand the structure
          const pelliData = contrastSensitivityData as {
            pelliRobsonLetterSet?: ScoredLetterSet[]; // Changed from string to array
            pelliRobsonIndex?: number;
          };

          // Create updated data merging existing data with new data
          const updatedPelliRobsonData = {
            ...existingPelliRobsonData,
            // Only update letterSet if it's in the incoming data
            ...(pelliData.pelliRobsonLetterSet && {
              pelliRobsonLetterSet: pelliData.pelliRobsonLetterSet,
            }),
            // Only update index if it's in the incoming data
            ...(pelliData.pelliRobsonIndex !== undefined && {
              pelliRobsonIndex: pelliData.pelliRobsonIndex,
            }),
          };

          const existingContrast = state.collectedTestData?.contrastSensitivityOrPelliRobsonData;
          state.collectedTestData = {
            ...state.collectedTestData,
            contrastSensitivityOrPelliRobsonData: {
              ...existingContrast,
              contrastSensitivityDatas: null,
              pelliRobsonData: updatedPelliRobsonData,
            },
          };
        } else {
          // Regular contrast sensitivity data (non-Pelli-Robson)
          const existing = state.collectedTestData?.contrastSensitivityOrPelliRobsonData;
          const contrastData = contrastSensitivityData as ContrastSensitivityData;
          const isThreshold = state.configuration?.testType === TestType.Threshold;
          state.collectedTestData = {
            ...state.collectedTestData,
            contrastSensitivityOrPelliRobsonData: {
              ...existing,
              contrastSensitivityDatas: [
                ...(existing?.contrastSensitivityDatas || []),
                contrastData,
              ],
              ...(isThreshold && contrastData.fpFnInfo && { fpFnInfo: contrastData.fpFnInfo }),
            },
          };
        }
      }
      if (pupillometryData) {
        state.collectedTestData = {
          ...state.collectedTestData,
          pupillometryDatas: [
            ...(state.collectedTestData?.pupillometryDatas || []),
            pupillometryData,
          ],
        };
      }
      if (ocularMotilityData) {
        state.collectedTestData = {
          ...state.collectedTestData,
          ocularMotilityDatas: [
            ...(state.collectedTestData?.ocularMotilityDatas || []),
            ocularMotilityData,
          ],
        };
      }
      if (ocularMotilityMetrics) {
        state.collectedTestData = {
          ...state.collectedTestData,
          ocularMotilityMetrics: ocularMotilityMetrics,
        };
      }
      if (vAcuityLetterSet) {
        state.collectedTestData = {
          ...state.collectedTestData,
          vAcuityLetterSets: vAcuityLetterSet,
        };
      }
      if (vAcuityIndex) {
        state.collectedTestData = {
          ...state.collectedTestData,
          vAcuityIndex: vAcuityIndex,
        };
      }
      const existingTumblingEData = state.collectedTestData?.visualAcuitySnapshots;
      const resolvedThresholdRow =
        tumblingEThresholdRow !== undefined
          ? tumblingEThresholdRow
          : (existingTumblingEData?.thresholdRow ?? null);
      const resolvedThresholdSnellenSize =
        tumblingEThresholdSnellenSize !== undefined
          ? tumblingEThresholdSnellenSize
          : (existingTumblingEData?.thresholdSnellenSize ?? null);
      const resolvedChartStimuli =
        tumblingEChartStimuli !== undefined
          ? tumblingEChartStimuli
          : existingTumblingEData?.chartStimuli;
      if (tumblingESnapshot?.status == TumblingEStatus.CURRENTLY_DISPLAYED) {
        state.collectedTestData = {
          ...state.collectedTestData,
          visualAcuitySnapshots: {
            tumblingESnapshots: [...(existingTumblingEData?.tumblingESnapshots || [])],
            currentlyActiveTumblingE: tumblingESnapshot,
            thresholdRow: resolvedThresholdRow,
            thresholdSnellenSize: resolvedThresholdSnellenSize,
            chartStimuli: resolvedChartStimuli,
          },
        };
      } else if (tumblingESnapshot) {
        state.collectedTestData = {
          ...state.collectedTestData,
          visualAcuitySnapshots: {
            tumblingESnapshots: [
              ...(existingTumblingEData?.tumblingESnapshots || []),
              tumblingESnapshot,
            ],
            // If the incoming snapshot is a response to the latest displayed Tumbling E,
            // i.e. they have the same _id,
            // that means the active tumbling e is no longer active
            // so we set it to null
            currentlyActiveTumblingE:
              existingTumblingEData?.currentlyActiveTumblingE?._id === tumblingESnapshot._id
                ? null
                : existingTumblingEData?.currentlyActiveTumblingE || null,
            thresholdRow: resolvedThresholdRow,
            thresholdSnellenSize: resolvedThresholdSnellenSize,
            chartStimuli: resolvedChartStimuli,
          },
        };
      } else if (
        tumblingEThresholdRow !== undefined ||
        tumblingEThresholdSnellenSize !== undefined ||
        tumblingEChartStimuli !== undefined
      ) {
        state.collectedTestData = {
          ...state.collectedTestData,
          visualAcuitySnapshots: {
            tumblingESnapshots: [...(existingTumblingEData?.tumblingESnapshots || [])],
            currentlyActiveTumblingE: existingTumblingEData?.currentlyActiveTumblingE || null,
            thresholdRow: resolvedThresholdRow,
            thresholdSnellenSize: resolvedThresholdSnellenSize,
            chartStimuli: resolvedChartStimuli,
          },
        };
      }
      if (fdtData) {
        const existingFDTData = state.collectedTestData?.fdtData;
        if (typeof fdtData === "boolean") {
          state.collectedTestData = {
            ...state.collectedTestData,
            fdtData: existingFDTData
              ? { ...existingFDTData, firstButtonPressed: fdtData }
              : { firstButtonPressed: fdtData, fdtDatas: [] },
          };
        } else if (Array.isArray(fdtData)) {
          state.collectedTestData = {
            ...state.collectedTestData,
            fdtData: {
              ...existingFDTData,
              fdtDatas: [...(existingFDTData?.fdtDatas || []), ...fdtData],
            },
          };
        }
      }
      if (coverTestData) {
        const existingCoverTestData = state.collectedTestData?.coverTestData || {
          [EyeCoverAction.CoverLeftEyeRightKeptOpen]: [],
          [EyeCoverAction.CoverRightEyeLeftKeptOpen]: [],
          [EyeCoverAction.OpenLeftEyeRightKeptOpen]: [],
          [EyeCoverAction.OpenRightEyeLeftKeptOpen]: [],
          leftDistanceData: [],
          rightDistanceData: [],
          referenceDatas: [],
          objectivePhoria: undefined,
        };
        state.collectedTestData = {
          ...state.collectedTestData,
          coverTestData: {
            [EyeCoverAction.CoverLeftEyeRightKeptOpen]: [
              ...existingCoverTestData[EyeCoverAction.CoverLeftEyeRightKeptOpen],
              ...(coverTestData[EyeCoverAction.CoverLeftEyeRightKeptOpen] || []),
            ],
            [EyeCoverAction.CoverRightEyeLeftKeptOpen]: [
              ...existingCoverTestData[EyeCoverAction.CoverRightEyeLeftKeptOpen],
              ...(coverTestData[EyeCoverAction.CoverRightEyeLeftKeptOpen] || []),
            ],
            [EyeCoverAction.OpenLeftEyeRightKeptOpen]: [
              ...existingCoverTestData[EyeCoverAction.OpenLeftEyeRightKeptOpen],
              ...(coverTestData[EyeCoverAction.OpenLeftEyeRightKeptOpen] || []),
            ],
            [EyeCoverAction.OpenRightEyeLeftKeptOpen]: [
              ...existingCoverTestData[EyeCoverAction.OpenRightEyeLeftKeptOpen],
              ...(coverTestData[EyeCoverAction.OpenRightEyeLeftKeptOpen] || []),
            ],

            leftDistanceData: [
              ...existingCoverTestData.leftDistanceData,
              ...(coverTestData.leftDistanceData || []),
            ],
            rightDistanceData: [
              ...existingCoverTestData.rightDistanceData,
              ...(coverTestData.rightDistanceData || []),
            ],

            referenceDatas: [
              ...existingCoverTestData.referenceDatas,
              ...(coverTestData.referenceDatas || []),
            ],
            objectivePhoria: coverTestData.objectivePhoria || existingCoverTestData.objectivePhoria,
          },
        };
      }
      if (cptSnapshot) {
        const existingCptData = state.collectedTestData?.cptData || {
          stimuli: [],
          responses: [],
          headVelocityDatas: [],
          gazePercentageData: [],
          pupilSizeData: [],
        };

        state.collectedTestData = {
          ...state.collectedTestData,
          cptData: {
            stimuli: [
              ...(existingCptData.stimuli || []),
              ...(cptSnapshot.stimulus ? [cptSnapshot.stimulus] : []),
            ],
            responses: [
              ...(existingCptData.responses || []),
              ...(cptSnapshot.stimulusResponse ? [cptSnapshot.stimulusResponse] : []),
            ],
            headVelocityDatas: [
              ...(existingCptData.headVelocityDatas || []),
              ...(cptSnapshot.headVelocityData ? [cptSnapshot.headVelocityData] : []),
            ],
            gazePercentageData: [
              ...(existingCptData.gazePercentageData || []),
              ...(cptSnapshot.percentagePoint ? [cptSnapshot.percentagePoint] : []),
            ],
            eyeDisplacements: [
              ...(existingCptData.eyeDisplacements || []),
              ...(cptSnapshot.percentagePoint && cptSnapshot.percentagePoint.timeStamp
                ? [
                    {
                      distance: calculateDisplacementFromOrigin(cptSnapshot.percentagePoint),
                      timeStamp: cptSnapshot.percentagePoint.timeStamp,
                    },
                  ]
                : []),
            ],
            pupilSizeData: [
              ...(existingCptData.pupilSizeData || []),
              ...(cptSnapshot.pupilSizeData ? [cptSnapshot.pupilSizeData] : []),
            ],
            stageEventList: [
              ...(existingCptData.stageEventList || []),
              ...(cptSnapshot.stageEvent ? [cptSnapshot.stageEvent] : []),
            ],
            isForAdult: cptSnapshot.isForAdult ?? existingCptData.isForAdult,
          },
        };
      }
      if (stereoAcuityData) {
        state.collectedTestData = {
          ...state.collectedTestData,
          stereoAcuityDatas: [
            ...(state.collectedTestData?.stereoAcuityDatas || []),
            stereoAcuityData,
          ],
        };
      }
      if (eyeImageData) {
        state.collectedTestData = {
          ...state.collectedTestData,
          latestEyeImageSet: {
            leftEye:
              eyeImageData.eyeType === EyeType.LeftEye
                ? eyeImageData
                : state.collectedTestData?.latestEyeImageSet?.leftEye,
            rightEye:
              eyeImageData.eyeType === EyeType.RightEye
                ? eyeImageData
                : state.collectedTestData?.latestEyeImageSet?.rightEye,
          },
        };
      }
      if (kineticStimulus) {
        state.collectedTestData = {
          ...state.collectedTestData,
          kineticData: {
            ...state.collectedTestData?.kineticData, // Preserve all existing kinetic data
            lastStimulus: !kineticStimulus.isResponse
              ? kineticStimulus
              : state.collectedTestData?.kineticData?.lastStimulus,
            responses: kineticStimulus.isResponse
              ? [...(state.collectedTestData?.kineticData?.responses || []), kineticStimulus]
              : state.collectedTestData?.kineticData?.responses || [],
          },
        };
      } else if (kineticGraphStat) {
        (state.collectedTestData as CollectedTestData) = {
          ...state.collectedTestData,
          kineticData: {
            ...(state.collectedTestData?.kineticData as KineticPerimetryCollectedData),
            kineticGraphStat: kineticGraphStat as KineticGraphStat,
          } as KineticPerimetryCollectedData,
        };
      } else if (vfaSnapshot) {
        state.collectedTestData = {
          ...state.collectedTestData,
          vfaCollectedData: ConvertVFASnapshotToCollectedData(
            vfaSnapshot,
            state.collectedTestData?.vfaCollectedData as VFACollectedData,
          ),
        };
      } else if (colorVisionSnapshot || colorVisionResults) {
        const existingData = state.collectedTestData?.colorVisionData;
        const existingResults = existingData?.results || [];
        const newResults =
          colorVisionResults ??
          (colorVisionSnapshot ? [...existingResults, colorVisionSnapshot] : existingResults);
        const latestSnapshot =
          colorVisionSnapshot ??
          (colorVisionResults && colorVisionResults.length > 0
            ? colorVisionResults[colorVisionResults.length - 1]
            : existingData?.latestSnapshot);
        state.collectedTestData = {
          ...state.collectedTestData,
          colorVisionData: {
            ...existingData,
            latestSnapshot,
            results: newResults,
          },
        };
      } else if (colorVisionInitData) {
        const existingData = state.collectedTestData?.colorVisionData;
        state.collectedTestData = {
          ...state.collectedTestData,
          colorVisionData: {
            ...existingData,
            initData: colorVisionInitData,
          },
        };
      } else if (dyscalculiaSnapshot) {
        const existingTasks: DyscalculiaTaskResult[] =
          state.collectedTestData?.dyscalculiaTaskResults || [];
        const incomingTaskKey = dyscalculiaSnapshot.taskId ?? dyscalculiaSnapshot.taskType;
        const taskIndex = existingTasks.findIndex(
          (task) => (task.taskId ?? task.taskType) === incomingTaskKey,
        );

        const updatedTasks: DyscalculiaTaskResult[] =
          taskIndex >= 0
            ? existingTasks.map((task, idx) =>
                idx === taskIndex
                  ? { ...task, results: [...(task.results || []), dyscalculiaSnapshot.result] }
                  : task,
              )
            : [
                ...existingTasks,
                {
                  taskType: dyscalculiaSnapshot.taskType,
                  taskId: dyscalculiaSnapshot.taskId,
                  results: [dyscalculiaSnapshot.result],
                },
              ];

        state.collectedTestData = {
          ...state.collectedTestData,
          dyscalculiaTaskResults: updatedTasks,
        };
      } else if (wcstSnapshot) {
        const existing: {
          trials: import("types/testTypes").WCSTTrial[];
          averageReactionTimeMs?: number | null;
          averageTrialsToFindRule?: number | null;
        } = state.collectedTestData?.wcstData ?? {
          trials: [],
          averageReactionTimeMs: null,
          averageTrialsToFindRule: null,
        };
        let trials = existing.trials ?? [];
        if (wcstSnapshot.trial) {
          trials = [...trials, wcstSnapshot.trial];
        }
        const averageReactionTimeMs =
          wcstSnapshot.averageReactionTimeMs !== undefined
            ? wcstSnapshot.averageReactionTimeMs
            : existing.averageReactionTimeMs;
        const averageTrialsToFindRule =
          wcstSnapshot.averageTrialsToFindRule !== undefined
            ? wcstSnapshot.averageTrialsToFindRule
            : existing.averageTrialsToFindRule;
        state.collectedTestData = {
          ...state.collectedTestData,
          wcstData: {
            trials,
            averageReactionTimeMs: averageReactionTimeMs ?? null,
            averageTrialsToFindRule: averageTrialsToFindRule ?? null,
          },
        };
      } else if (readingAssessmentSnapshot?.comprehensionAnswers?.length) {
        state.collectedTestData = {
          ...state.collectedTestData,
          readingAssessmentComprehensionAnswers: readingAssessmentSnapshot.comprehensionAnswers,
        };
      } else if (npctSnapshot) {
        state.collectedTestData = {
          ...state.collectedTestData,
          npctSnapshots: [...(state.collectedTestData?.npctSnapshots ?? []), npctSnapshot],
        };
      } else if (oknSnapshot) {
        const existing = state.collectedTestData?.oknData;
        const latestSample = oknSnapshot.latestSample as OKNRawSample | null | undefined;
        state.collectedTestData = {
          ...state.collectedTestData,
          oknData: {
            snapshots: [...(existing?.snapshots ?? []), oknSnapshot as OKNProgressSnapshot],
            rawSamples: [...(existing?.rawSamples ?? []), ...(latestSample ? [latestSample] : [])],
            latestSnapshot: oknSnapshot as OKNProgressSnapshot,
          },
        };
      } else if (smoothPursuitSnapshot) {
        state.collectedTestData = {
          ...state.collectedTestData,
          smoothPursuitSnapshots: [
            ...(state.collectedTestData?.smoothPursuitSnapshots ?? []),
            smoothPursuitSnapshot,
          ],
        };
      }
      if (npctUserInput) {
        state.collectedTestData = {
          ...state.collectedTestData,
          npctUserInputs: [...(state.collectedTestData?.npctUserInputs ?? []), npctUserInput],
        };
      }
      if (antiSaccadeSample) {
        state.collectedTestData = {
          ...state.collectedTestData,
          antiSaccadeSamples: [
            ...(state.collectedTestData?.antiSaccadeSamples ?? []),
            antiSaccadeSample,
          ],
        };
      }
    },
    setTestCompletedPercentage(state, action: PayloadAction<number>) {
      state.completedPercentage = action.payload;
    },
    updatePupilSizeData(state, action: PayloadAction<PupilSizeData>) {
      const newPupilData = action.payload;
      state.collectedTestData = {
        ...state.collectedTestData,
        pupilSizeData: [...(state.collectedTestData?.pupilSizeData || []), newPupilData],
      };
    },
    setIsCurrentlyCalibrating(state, action: PayloadAction<boolean>) {
      state.isCurrentlyCalibrating = action.payload;
    },
    setCalibrationUnresponsive(state, action: PayloadAction<boolean>) {
      state.calibrationUnresponsive = action.payload;
    },
    setCalibrationLifecycle(state, action: PayloadAction<CalibrationLifecycleStatus | null>) {
      state.calibrationUnresponsive = false;
      state.calibrationLifecycle = action.payload;
      if (!action.payload) {
        state.calibrationFailedInRun = false;
        return;
      }

      // sceneOpened is emitted once when the calibration scene opens and never
      // again by a retry, so it is the one point where a failure persisted from
      // an earlier run can be dropped without breaking the stickiness of the
      // unlock across retries.
      if (action.payload.status === "sceneOpened") {
        state.calibrationFailedInRun = false;
        return;
      }

      if (action.payload.status === "failed" || action.payload.outcome === "TimedOut") {
        state.calibrationFailedInRun = true;
      }
    },
    setCurrentVirtualAssistanceVideo(state, action: PayloadAction<string | null>) {
      state.currentVirtualAssistanceVideo = action.payload;
    },
    setVirtualAssistanceStatus(state, action: PayloadAction<VirtualAssistanceStatus | null>) {
      const doNotShowAgainList = state.virtualAssistanceStatus?.doNotShowAgainList;
      state.virtualAssistanceStatus = action.payload;
      // Carry the do-not-show-again list across status updates, but only when
      // the new payload is an object — payload can be null (status cleared),
      // and writing onto null would throw.
      if (doNotShowAgainList && state.virtualAssistanceStatus) {
        state.virtualAssistanceStatus.doNotShowAgainList = doNotShowAgainList;
      }
    },
    setVirtualAssistantConfigVideoList(state, action: PayloadAction<VirtualAssistantVideo[]>) {
      if (state.configuration && state.configuration.virtualAssistanceConfig) {
        state.configuration.virtualAssistanceConfig.videoInfos = action.payload;
      } else {
        console.warn(
          "Trying to set virtual assistant video list but test configuration or virtual assistance config is null",
        );
      }
    },
    addVideosToDoNotShowAgainList(state, action: PayloadAction<string[]>) {
      const videoNames = action.payload;
      if (!state.virtualAssistanceStatus) {
        state.virtualAssistanceStatus = {};
      }
      if (!state.virtualAssistanceStatus.doNotShowAgainList) {
        state.virtualAssistanceStatus.doNotShowAgainList = [];
      }
      if (videoNames && videoNames.length > 0) {
        state.virtualAssistanceStatus.doNotShowAgainList = [
          ...new Set([...state.virtualAssistanceStatus.doNotShowAgainList, ...videoNames]),
        ];
      }
    },
    clearDoNotShowAgainList(state) {
      if (state.virtualAssistanceStatus) {
        state.virtualAssistanceStatus.doNotShowAgainList = [];
      }
    },
    clearCollectedTestData(state) {
      state.collectedTestData = null;
    },
    updateSavedTestConfiguration(
      state,
      action: PayloadAction<Partial<
        Record<TestCategory, Partial<TestConfiguration> | null>
      > | null>,
    ) {
      state.savedConfiguration = action.payload;
    },
    resetSavedTestConfiguration(_state) {},
    saveTestConfiguration() {},
    loadSavedTestConfiguration(_state) {},
    updateTimeScale(state, action: PayloadAction<number>) {
      state.timeScale = action.payload;
    },
    testConfigurationUpdateComplete(_state) {},
    setIsSceneReady(state, action: PayloadAction<boolean>) {
      state.isSceneReady = action.payload;
    },
    setTestPhase(state, action: PayloadAction<string | null>) {
      state.testPhase = action.payload;
    },
    setHeadsetActiveTest(state, action: PayloadAction<HeadsetActiveTest | null>) {
      state.headsetActiveTest = action.payload;
    },
    setShouldWaitForNextEyeOnFinish(state, action: PayloadAction<boolean>) {
      state.shouldWaitForNextEyeOnFinish = action.payload;
    },
    startTestingNextEyeInBackToBackTest(_state, _action: PayloadAction<EyeType>) {},
    setIsLastBackToBackEyeTest(state, action: PayloadAction<boolean>) {
      state.isLastOfBackToBackEyeTest = action.payload;
    },
    setManualCompletionCheckInProgress(state, action: PayloadAction<boolean>) {
      state.isManualCompletionCheckInProgress = action.payload;
    },
    setManualCompletionCheckFailed(state, action: PayloadAction<boolean>) {
      state.isManualCompletionCheckFailed = action.payload;
    },
    setManualCompletionCheckSucceeded(state, action: PayloadAction<boolean>) {
      state.isManualCompletionCheckSucceeded = action.payload;
    },
    manualCheckTestCompletion(_state, _action: PayloadAction<void>) {},
    triggerVfaReliabilityAutoRestart(_state) {},
  },
});

export const {
  clearTestRelatedData,
  setTestId,
  selectTest,
  setTestUIConfiguration,
  updateTestConfiguration,
  mergeProgressConfigToMain,
  clearTestConfigurations,
  setTestProgressConfiguration,
  setTestEventCodes,
  applyAvailableConfigurations,
  setAvailableConfigurations,
  setBaseAvailableConfigurations,
  resumeTest,
  setTestStatus,
  manuallyUpdateCollectedTestData,
  updateCollectedTestData,
  setTestCompletedPercentage,
  resetTestState,
  sendMessageToHeadSet,
  updatePupilSizeData,
  updateGazeData,
  setAvailableProgressConfigurations,
  setReadingAssessmentCatalog,
  setReadingAssessmentCatalogError,
  setIsGazeOffBounds,
  pauseTest,
  unpauseTest,
  resetPauseState,
  setIsCurrentlyCalibrating,
  setCalibrationLifecycle,
  setCalibrationUnresponsive,
  setCurrentVirtualAssistanceVideo,
  setVirtualAssistanceStatus,
  addVideosToDoNotShowAgainList,
  setVirtualAssistantConfigVideoList,
  clearDoNotShowAgainList,
  clearCollectedTestData,
  sendTestStartMessage,
  attachToRunningTest,
  sendTestTerminateMessage,
  setTestConfigurationExtraData,
  updatePersistedConfigFromTestConfig,
  updateSavedTestConfiguration,
  resetSavedTestConfiguration,
  saveTestConfiguration,
  loadSavedTestConfiguration,
  updateTimeScale,
  testConfigurationUpdateComplete,
  setIsSceneReady,
  setTestPhase,
  setHeadsetActiveTest,
  setShouldWaitForNextEyeOnFinish,
  startTestingNextEyeInBackToBackTest,
  setIsLastBackToBackEyeTest,
  setManualCompletionCheckInProgress,
  setManualCompletionCheckFailed,
  setManualCompletionCheckSucceeded,
  manualCheckTestCompletion,
  triggerVfaReliabilityAutoRestart,
} = testSlice.actions;

export default testSlice.reducer;
